import {AxiosError} from "axios";
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from "chart.js";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {WebmailApi, WebmailPortalControllerGetQuotaFolderEnum} from "components/src/api/portal/generated";
import {DashboardTable} from "components/src/dashboard/DashboardTable";
import {DashboardTableRow} from "components/src/dashboard/DashboardTableRow";
import {usePortalDashboardContext} from "components/src/hook/UsePortalDashboardContext";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {Utils} from "components/src/utils/Utils";
import {Mail as MailIcon, Settings as SettingsIcon} from "iconoir-react";
import {useEffect, useRef, useState} from "react";
import {Card, CardGroup} from "react-bootstrap";
import Row from "react-bootstrap/esm/Row";
import {Pie} from "react-chartjs-2";
import {useEnterprisePortalPropertiesContext} from "../hook/UseEnterprisePortalPropertiesContext";

ChartJS.register(ArcElement, Tooltip, Legend);

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());

export const EnterprisePortalDashboardExtension = () =>
{
    /**
     * The number of available messages
     */
    const [messageCount, setMessageCount] = useState<number>();
    /**
     * The number of unread messages
     */
    const [unreadCount, setUnreadCount] = useState<number>();
    /**
     * Quota usage in bytes, undefined if quota is not available
     */
    const [quotaUsage, setQuotaUsage] = useState<number>();
    /**
     * Quota limit in bytes, undefined if quota is not available
     */
    const [quotaLimit, setQuotaLimit] = useState<number>();
    /**
     * Enterprise Portal properties context
     */
    const {properties} = useEnterprisePortalPropertiesContext();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Represents the context object for the portal dashboard.
     * This variable is typically used to store and manage the state,
     * configurations, and functionalities associated with the portal dashboard.
     */
    const {stateCounter, setErrorMessage} = usePortalDashboardContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Reference to the Pie chart instance, used to trigger a resize after the quota data loads
     * so Chart.js recalculates its dimensions once the flexbox layout has settled.
     */
    const chartRef = useRef<ChartJS<'pie'>>(null);

    useEffect(() =>
    {
        chartRef.current?.resize();
    }, [quotaUsage,
        quotaLimit]);

    useEffect(() =>
    {
        if (!properties?.webmailEnabled) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetMessageCount(WebmailPortalControllerGetQuotaFolderEnum.Inbox, false)
            .then(response => {
                setMessageCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [stateCounter,
        t,
        setSpinnerStateCounter,
        setErrorMessage,
        properties?.webmailEnabled]);

    useEffect(() =>
    {
        if (!properties?.webmailEnabled) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetMessageCount(WebmailPortalControllerGetQuotaFolderEnum.Inbox, true)
            .then(response => {
                setUnreadCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [stateCounter,
        t,
        setSpinnerStateCounter,
        setErrorMessage,
        properties?.webmailEnabled]);

    useEffect(() =>
    {
        if (!properties?.webmailEnabled) {
            return;
        }

        webmailApi.webmailPortalControllerGetQuota(WebmailPortalControllerGetQuotaFolderEnum.Inbox)
            .then(response =>
            {
                const resource = response.data[0]?.resource[0];

                if (resource && resource.limit > 0)
                {
                    setQuotaUsage(resource.usage * 1024);
                    setQuotaLimit(resource.limit * 1024);
                }
            })
            .catch((err: Error | AxiosError) => {
                setErrorMessage(Utils.getErrorMessage(err, t));
            });
    }, [stateCounter,
        t,
        setSpinnerStateCounter,
        setErrorMessage,
        properties?.webmailEnabled]);

    const renderQuotaChart = () =>
    {
        if (quotaUsage === undefined || quotaLimit === undefined) {
            return null;
        }

        const used = quotaUsage;
        const free = Math.max(0, quotaLimit - used);
        const percentage = Math.min(100, Math.round((used / quotaLimit) * 100));
        const color = percentage >= 95 ? '#dc3545' : percentage >= 80 ? '#ffc107' : '#198754';

        const data = {
            labels: [t('portal-dashboard-quota-used'), t('portal-dashboard-quota-free')],
            datasets: [{
                data: [used, free],
                backgroundColor: [color, '#e9ecef'],
                borderWidth: 0,
            }],
        };

        const options = {
            maintainAspectRatio: false,
            plugins: {
                legend: {position: 'bottom' as const},
                tooltip: {
                    callbacks: {
                        label: (item: {raw: unknown}) =>
                            `${Utils.formatBytes(item.raw as number)}`,
                    },
                },
            },
        };

        return (
            <div className="cm-quota-chart">
                <Pie ref={chartRef} data={data} options={options}/>
            </div>
        );
    }

    return (
      <Row className="mt-2">
          {properties?.webmailEnabled &&
              <CardGroup>
                  <Card className="me-4">
                      <Card.Header><MailIcon className="text-success"/> {t('portal-dashboard-emails')}</Card.Header>
                      <Card.Body>
                          <div className="hstack gap-4 align-items-start">
                              <DashboardTable>
                                  <DashboardTableRow name={t('portal-dashboard-inbox-count')}
                                                     value={messageCount}
                                                     linkProps={{to: "/messages/inbox", state: {unreadOnly: false}}}
                                                     className="text-success"/>
                                  <DashboardTableRow name={t('portal-dashboard-unread-count')}
                                                     value={unreadCount}
                                                     linkProps={{to: "/messages/inbox", state: {unreadOnly: true}}}
                                                     className="text-success"/>
                              </DashboardTable>
                          </div>
                      </Card.Body>
                  </Card>

                  <Card className="border-start">
                      <Card.Header><SettingsIcon className="text-success"/> {t('portal-dashboard-quota')}</Card.Header>
                      <Card.Body className="d-flex">
                          <div className="align-items-start">
                              {renderQuotaChart()}
                          </div>
                      </Card.Body>
                  </Card>
              </CardGroup>
          }
      </Row>
    );
}
